import { useEffect, useMemo, useState } from "react";

import {
	api,
	ApiError,
	type AgentDirectoryEntry,
	type BusinessDTO,
} from "../lib/api.ts";

/**
 * Modal that scaffolds a new project-agent through a PR against
 * agents-extra.json. After merge + auto-deploy, the agent appears in the
 * dashboard and is runnable via `npx -y @theg1group/agent run <key>`.
 *
 * The owner must already have a tasks.g1.network project (we don't auto-create
 * one — that's a separate system). The flow:
 *   1. Pick a business (must already exist).
 *   2. Fill in key + display name + tasksProjectId + system prompt + AGENT.md.
 *   3. Submit → PR opened → review + merge in GitHub → auto-deploy makes the
 *      agent live within a minute.
 */
export function NewAgentModal({
	onClose,
	defaultBusinessId,
}: {
	onClose: () => void;
	defaultBusinessId?: string;
}) {
	const [businesses, setBusinesses] = useState<BusinessDTO[] | null>(null);
	const [allAgents, setAllAgents] = useState<AgentDirectoryEntry[]>([]);

	const [businessId, setBusinessId] = useState(defaultBusinessId ?? "");
	const [key, setKey] = useState("");
	const [displayName, setDisplayName] = useState("");
	const [parent, setParent] = useState<string>("");
	const [tasksProjectId, setTasksProjectId] = useState("");
	const [systemPrompt, setSystemPrompt] = useState(DEFAULT_PROMPT);
	const [agentMd, setAgentMd] = useState(DEFAULT_AGENT_MD);

	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [prUrl, setPrUrl] = useState<string | null>(null);
	const [autoMerged, setAutoMerged] = useState<boolean>(false);

	useEffect(() => {
		api.listBusinesses().then((r) => {
			setBusinesses(r.businesses);
			if (!businessId && r.businesses[0]) setBusinessId(r.businesses[0].id);
		});
		api.listAgents().then((r) => setAllAgents(r.agents));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const parentOptions = useMemo(
		() => allAgents.filter((a) => a.businessId === businessId),
		[allAgents, businessId],
	);

	function suggestKey(name: string) {
		const slug = name
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/^-|-$/g, "")
			.slice(0, 64);
		if (key === "") setKey(slug);
		setDisplayName(name);
	}

	async function handleCreate() {
		setBusy(true);
		setError(null);
		try {
			const result = await api.createAgent({
				key,
				displayName,
				businessId,
				parent: parent === "" ? null : parent,
				tasksProjectId,
				systemPrompt,
				agentMd,
			});
			setPrUrl(result.htmlUrl);
			setAutoMerged(Boolean(result.merged));
		} catch (err) {
			if (err instanceof ApiError && err.status === 409) {
				setError(`Agent key "${key}" is already taken.`);
			} else if (err instanceof ApiError && err.status === 400) {
				setError(`Invalid input — ${err.message}`);
			} else {
				setError((err as Error).message);
			}
		} finally {
			setBusy(false);
		}
	}

	const isValidUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
		tasksProjectId,
	);
	const canSubmit =
		!busy &&
		!prUrl &&
		key.length >= 2 &&
		displayName.length >= 1 &&
		businessId.length >= 1 &&
		isValidUuid &&
		systemPrompt.trim().length > 0 &&
		agentMd.trim().length > 0;

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
			onClick={onClose}
		>
			<div
				className="w-full max-w-2xl max-h-[90vh] flex flex-col border border-zinc-800 rounded-lg bg-terminal shadow-xl"
				onClick={(e) => e.stopPropagation()}
			>
				<header className="px-4 py-3 border-b border-zinc-800 flex items-baseline justify-between">
					<h2 className="text-base font-semibold">New project-agent</h2>
					<button
						type="button"
						onClick={onClose}
						className="text-xs text-zinc-500 hover:text-zinc-300"
					>
						close
					</button>
				</header>
				<div className="p-4 space-y-3 overflow-y-auto">
					{prUrl ? (
						<div className="text-sm text-emerald-300 space-y-2">
							<div>
								{autoMerged
									? "✓ Auto-merged. Agent goes live with the next auto-deploy (~1 min)."
									: "✓ PR opened — auto-merge didn't take, finish it manually."}
							</div>
							<a
								href={prUrl}
								target="_blank"
								rel="noreferrer"
								className="underline break-all"
							>
								{prUrl}
							</a>
							<div className="text-xs text-zinc-500">
								Then run on any host:{" "}
								<code className="bg-zinc-900 px-1.5 py-0.5 rounded">
									npx -y @theg1group/agent run {key}
								</code>
							</div>
						</div>
					) : (
						<>
							<div className="grid grid-cols-2 gap-3">
								<div>
									<label className="text-xs text-zinc-500">Business</label>
									<select
										value={businessId}
										onChange={(e) => setBusinessId(e.target.value)}
										className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm focus:outline-none focus:border-zinc-500"
										disabled={busy || !businesses}
									>
										{businesses?.map((b) => (
											<option key={b.id} value={b.id}>
												{b.name} ({b.id})
											</option>
										)) ?? <option>Loading…</option>}
									</select>
								</div>
								<div>
									<label className="text-xs text-zinc-500">Parent agent (optional)</label>
									<select
										value={parent}
										onChange={(e) => setParent(e.target.value)}
										className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm focus:outline-none focus:border-zinc-500"
										disabled={busy}
									>
										<option value="">— top level —</option>
										{parentOptions.map((a) => (
											<option key={a.key} value={a.key}>
												{a.displayName} ({a.key})
											</option>
										))}
									</select>
								</div>
							</div>
							<div>
								<label className="text-xs text-zinc-500">Display name</label>
								<input
									type="text"
									value={displayName}
									onChange={(e) => suggestKey(e.target.value)}
									placeholder="G1 Marketing Agent"
									autoFocus
									className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-zinc-500"
									disabled={busy}
								/>
							</div>
							<div>
								<label className="text-xs text-zinc-500">Key (slug, immutable)</label>
								<input
									type="text"
									value={key}
									onChange={(e) => setKey(e.target.value.toLowerCase())}
									placeholder="g1-marketing"
									className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm font-mono placeholder:text-zinc-600 focus:outline-none focus:border-zinc-500"
									disabled={busy}
								/>
								<div className="text-[11px] text-zinc-600 mt-1">
									Used as AGENT_LABEL and in URLs. Lowercase, digits, hyphens.
								</div>
							</div>
							<div>
								<label className="text-xs text-zinc-500">
									tasks.g1.network project_id (UUID)
								</label>
								<input
									type="text"
									value={tasksProjectId}
									onChange={(e) => setTasksProjectId(e.target.value)}
									placeholder="00000000-0000-0000-0000-000000000000"
									className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm font-mono placeholder:text-zinc-600 focus:outline-none focus:border-zinc-500"
									disabled={busy}
								/>
								<div className="text-[11px] text-zinc-600 mt-1">
									Create a project at{" "}
									<a
										href="https://tasks.g1.network/projects"
										target="_blank"
										rel="noreferrer"
										className="underline hover:text-zinc-400"
									>
										tasks.g1.network
									</a>{" "}
									first, then paste its UUID here.
								</div>
							</div>
							<div>
								<label className="text-xs text-zinc-500">System prompt</label>
								<textarea
									value={systemPrompt}
									onChange={(e) => setSystemPrompt(e.target.value)}
									rows={10}
									className="mt-1 w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-200 text-sm font-mono resize-y focus:outline-none focus:border-zinc-500"
									disabled={busy}
								/>
							</div>
							<div>
								<label className="text-xs text-zinc-500">AGENT.md</label>
								<textarea
									value={agentMd}
									onChange={(e) => setAgentMd(e.target.value)}
									rows={6}
									className="mt-1 w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-300 text-sm font-mono resize-y focus:outline-none focus:border-zinc-500"
									disabled={busy}
								/>
							</div>
							{error && <div className="text-xs text-red-300">{error}</div>}
						</>
					)}
				</div>
				<footer className="px-4 py-3 border-t border-zinc-800 flex justify-end gap-2">
					{prUrl ? (
						<button
							type="button"
							onClick={onClose}
							className="px-3 py-1.5 bg-accent-600 hover:bg-accent-500 text-white text-sm font-medium rounded"
						>
							Done
						</button>
					) : (
						<>
							<button
								type="button"
								onClick={onClose}
								disabled={busy}
								className="px-3 py-1.5 border border-zinc-700 hover:border-zinc-600 text-zinc-300 text-sm rounded"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={handleCreate}
								disabled={!canSubmit}
								className="px-3 py-1.5 bg-accent-600 hover:bg-accent-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-sm font-medium rounded"
							>
								{busy ? "Opening PR…" : "Open PR"}
							</button>
						</>
					)}
				</footer>
			</div>
		</div>
	);
}

const DEFAULT_PROMPT = `You are the <ROLE> for <BUSINESS>.

# Responsibilities
- Describe what this agent owns.

# Reporting
The manager-agent (agent.g1.group) assigns you tasks via the tasks.g1.network project. Reply with one comment per task.

# Tools
You currently have no MCP tools wired in. If you need data, request it explicitly in your reply and the manager-agent will follow up.
`;

const DEFAULT_AGENT_MD = `# <Agent name>

Brief description of what this agent does at runtime — what data it touches, what queue it watches, what kind of answers it produces.
`;
